import React, { useMemo, useState } from 'react';
import { Typography, Box, useTheme, Divider, styled } from '@mui/material';
import { EventAvailable, EventBusy, Warning } from '@mui/icons-material';

import { HotelCancelPolicyModal, HotelCancelPolicy } from './modal';
import { useTranslation } from 'react-i18next';
import { fromDateToDDMMYYYY } from '@frontend/features/common/helper';

export const LinkStyleButton = styled('button')(({ theme }) => ({
  all: 'unset', // resetea todo: sin borde, sin padding, sin fondo, etc.
  cursor: 'pointer',
  color: theme.palette.primary.main,
  ...theme.typography.body2,
  '&:hover': {
    textDecoration: 'underline',
  },
}));

type Props = {
  refundable: boolean;
  cancelPolicy: HotelCancelPolicy;
}

export const HotelCancelPolicyIndicator: React.FC<Props> = ({ refundable, cancelPolicy }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isModalOpen, setModalOpen] = useState(false);

  const { isRefundable, deadline, cancelationMessage } = useMemo(
    () => {
      let baseRefundable = refundable;
      let applicableDeadline = null;
      let applicablePenalty = null;

      if (baseRefundable && cancelPolicy?.cancelPenalties?.length) {
        const today = new Date();
        const futurePenalties = cancelPolicy.cancelPenalties.filter(penalty => {
          if (!penalty.deadline) return false;
          const penaltyDate = new Date(penalty.deadline);
          return penaltyDate > today;
        });

        if (futurePenalties.length > 0) {
          const hasZeroCostPenalty = futurePenalties.some(penalty => penalty.value === 0);

          if (hasZeroCostPenalty) {
            applicablePenalty = futurePenalties.find(penalty => penalty.value > 0);
          } else {
            applicablePenalty = futurePenalties[0];
          }
        } else {
          applicablePenalty = cancelPolicy.cancelPenalties.find(penalty => 
            penalty.deadline && penalty.value > 0
          ) || cancelPolicy.cancelPenalties.filter(penalty => penalty.deadline).slice(-1)[0];
        }

        applicableDeadline = applicablePenalty?.deadline || cancelPolicy.cancelPenalties[0]?.deadline;
      }

      let message;
      if (baseRefundable) {
        if (applicableDeadline) {
          const today = new Date();
          const deadlineDate = new Date(applicableDeadline);
          const shouldShowRefundable = !applicablePenalty || 
            applicablePenalty.value === 0 || 
            (applicablePenalty.value > 0 && deadlineDate > today);
          
          const color = shouldShowRefundable ? theme.palette.success.main : theme.palette.warning.main;
          const icon = shouldShowRefundable ? 
            <EventAvailable htmlColor={theme.palette.success.main} /> : 
            <Warning htmlColor={theme.palette.warning.main} />;

          message = {
            message: `${t('hotels.fares.cancelPolicy.deadline')} ${fromDateToDDMMYYYY(applicableDeadline)}`,
            color,
            icon,
          };
        } else {
          message = {
            message: t('hotels.fares.cancelPolicy.refundable'),
            color: theme.palette.warning.main,
            icon: <Warning htmlColor={theme.palette.warning.main} />,
          };
        }
      } else {
        message = {
          message: t('hotels.fares.cancelPolicy.nonRefundable'),
          color: theme.palette.error.main,
          icon: <EventBusy htmlColor={theme.palette.error.main} />,
        };
      }

      return { 
        isRefundable: baseRefundable, 
        deadline: applicableDeadline,
        cancelationMessage: message
      };
    },
    [refundable, cancelPolicy, theme, t]
  );


  return (
    <>
      {refundable && <HotelCancelPolicyModal
        open={isModalOpen}
        onClose={() => setModalOpen(false)}
        cancelPolicy = { cancelPolicy }
      />}
      <Box display="flex" gap={2} alignItems="center">
        <Box display="flex" gap={1} alignItems="center">
          {cancelationMessage.icon}
          <Typography variant="body2" component={'span'} color={cancelationMessage.color}>
            {cancelationMessage.message}
          </Typography>
        </Box>
        {(isRefundable && deadline) && <>
          <Divider orientation='vertical' />
          <LinkStyleButton onClick={() => setModalOpen(prev => !prev)} sx={{ flex: 1}}>
            { t('hotels.fares.cancelPolicy.details') }
          </LinkStyleButton>
        </>}
      </Box>
    </>
  );
};
